import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useOffline } from '../../context/OfflineContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { 
  Sun, AlertTriangle, ChevronRight, RefreshCw, Loader2, WifiOff,
  CheckCircle2, Flame, Calendar, Thermometer, Droplets, Wind, Cloud,
  Snowflake, CloudRain, Search, Home, Leaf, Zap, X, CalendarDays, Bell, Baby, Heart,
  MapPin, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import ChoreCalendar from '../ChoreCalendar';
import StarAnimalsGallery from '../ui/StarAnimalsGallery';
import { todayLocal, daysAgoLocal } from '../../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for weather suggestions
const suggestionIcons = {
  'snowflake': Snowflake,
  'droplet': Droplets,
  'leaf': Leaf,
  'thermometer': Thermometer,
  'sun': Sun,
  'cloud-rain': CloudRain,
  'home': Home,
  'wind': Wind,
  'search': Search,
  'flame': Flame,
};

const DailyAdvisor = () => {
  const { getAuthHeader, user } = useAuth();
  const { isOnline, cacheData, getCachedData } = useOffline();
  const [advice, setAdvice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isFromCache, setIsFromCache] = useState(false);
  
  // Checklist state
  const [checklist, setChecklist] = useState([]);
  const [checklistLoading, setChecklistLoading] = useState(true);
  const [checklistStats, setChecklistStats] = useState({ completed: 0, total: 0, streak: 0 });
  
  // Weather state
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  
  // Weather-based suggestions (Field Conditions)
  const [weatherSuggestions, setWeatherSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const [dismissedSuggestions, setDismissedSuggestions] = useState(() => {
    const today = todayLocal();
    const stored = localStorage.getItem('dismissedWeatherSuggestions');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.date === today) {
        return parsed.ids || [];
      }
    }
    return [];
  });
  
  // Calendar dialog
  const [showCalendar, setShowCalendar] = useState(false);

  // Farm Reminders state (upcoming events)
  const [farmReminders, setFarmReminders] = useState([]);
  const [remindersLoading, setRemindersLoading] = useState(true);

  const fetchAdvice = async (refresh = false) => {
    if (refresh) {
      setRefreshing(true);
      try {
        const res = await axios.post(`${API_URL}/api/advisor/refresh`, {}, {
          headers: getAuthHeader()
        });
        setAdvice(res.data);
        setIsFromCache(false);
        cacheData('daily_advice', res.data);
        toast.success('Updated!');
      } catch (error) {
        console.error('Failed to refresh advice:', error);
        toast.error('Failed to refresh');
      } finally {
        setRefreshing(false);
      }
    } else {
      const cached = getCachedData('daily_advice');
      if (cached) {
        setAdvice(cached);
        setIsFromCache(true);
        setLoading(false);
      }
      
      try {
        const res = await axios.get(`${API_URL}/api/advisor/daily`, {
          headers: getAuthHeader()
        });
        setAdvice(res.data);
        setIsFromCache(res.data.cached || false);
        cacheData('daily_advice', res.data);
      } catch (error) {
        console.error('Failed to fetch advice:', error);
        if (!cached) {
          toast.error('Failed to load daily overview');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchChecklist = async () => {
    setChecklistLoading(true);
    try {
      const [todayRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/chores/today`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/chores/history?days=7`, { headers: getAuthHeader() })
      ]);
      
      setChecklist(todayRes.data.chores || []);
      setChecklistStats({
        completed: todayRes.data.completed_count || 0,
        total: todayRes.data.total || 0,
        streak: historyRes.data.streak || 0
      });
    } catch (error) {
      console.error('Failed to fetch checklist:', error);
    } finally {
      setChecklistLoading(false);
    }
  };

  const fetchFarmReminders = async () => {
    setRemindersLoading(true);
    try {
      const [upcomingRes, todayRes] = await Promise.all([
        axios.get(`${API_URL}/api/reminders/upcoming?days_ahead=14`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/reminders/today`, { headers: getAuthHeader() })
      ]);
      
      setFarmReminders(upcomingRes.data.reminders || []);
      
      if (todayRes.data.auto_add_enabled && todayRes.data.checklist_tasks?.length > 0) {
        console.log('Farm tasks for today:', todayRes.data.checklist_tasks);
      }
    } catch (error) {
      console.error('Failed to fetch farm reminders:', error);
    } finally {
      setRemindersLoading(false);
    }
  };

  const fetchWeather = async () => {
    setWeatherLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/weather/current`, { headers: getAuthHeader() });
      setWeather(res.data);
      cacheData('current_weather', res.data);
    } catch (error) {
      console.error('Failed to fetch weather:', error);
      const cached = getCachedData('current_weather');
      if (cached) setWeather(cached);
    } finally {
      setWeatherLoading(false);
    }
  };

  const fetchWeatherSuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/weather/chore-suggestions`, { headers: getAuthHeader() });
      setWeatherSuggestions(res.data.suggestions || []);
      cacheData('weather_suggestions', res.data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch weather suggestions:', error);
      const cached = getCachedData('weather_suggestions');
      if (cached) setWeatherSuggestions(cached);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const toggleChore = async (choreId, currentCompleted) => {
    setChecklist(prev => prev.map(c => 
      c.id === choreId ? { ...c, completed: !currentCompleted } : c
    ));
    setChecklistStats(prev => ({
      ...prev,
      completed: currentCompleted ? prev.completed - 1 : prev.completed + 1
    }));

    try {
      await axios.post(`${API_URL}/api/chores/complete`, {
        chore_id: choreId,
        completed: !currentCompleted
      }, { headers: getAuthHeader() });
    } catch (error) {
      setChecklist(prev => prev.map(c => 
        c.id === choreId ? { ...c, completed: currentCompleted } : c
      ));
      setChecklistStats(prev => ({
        ...prev,
        completed: currentCompleted ? prev.completed : prev.completed - 1
      }));
      toast.error('Failed to update chore');
    }
  };

  const dismissSuggestion = (suggestionId) => {
    const newDismissed = [...dismissedSuggestions, suggestionId];
    setDismissedSuggestions(newDismissed);
    
    const today = todayLocal();
    localStorage.setItem('dismissedWeatherSuggestions', JSON.stringify({
      date: today,
      ids: newDismissed
    }));
    
    toast.success('Dismissed for today');
  };

  const activeSuggestions = weatherSuggestions.filter(
    s => !dismissedSuggestions.includes(s.id)
  );

  useEffect(() => {
    const cached = getCachedData('daily_advice');
    if (cached) {
      setAdvice(cached);
      setIsFromCache(true);
      setLoading(false);
    }
    
    fetchAdvice();
    fetchChecklist();
    fetchWeather();
    fetchWeatherSuggestions();
    fetchFarmReminders();
  }, []);

  const parseAdvice = (text) => {
    if (!text) return { actions: [], watchouts: [] };
    
    const actions = [];
    const watchouts = [];
    
    const lines = text.split('\n');
    let section = null;
    
    for (const line of lines) {
      const trimmed = line.trim();
      
      if (trimmed.toLowerCase().includes('top 3 actions') || trimmed.toLowerCase().includes('actions today') || trimmed.toLowerCase().includes('priority')) {
        section = 'actions';
        continue;
      }
      if (trimmed.toLowerCase().includes('watchout') || trimmed.toLowerCase().includes("week's") || trimmed.toLowerCase().includes('this week') || trimmed.toLowerCase().includes('watch')) {
        section = 'watchouts';
        continue;
      }
      
      if (trimmed.match(/^[\d\.\-\*•]+/) && trimmed.length > 3) {
        const content = trimmed.replace(/^[\d\.\-\*•\s]+/, '').trim();
        if (content && section === 'actions' && actions.length < 3) {
          actions.push(content);
        } else if (content && section === 'watchouts' && watchouts.length < 3) {
          watchouts.push(content);
        }
      }
    }
    
    return { actions, watchouts };
  };

  const { actions, watchouts } = parseAdvice(advice?.advice);
  const completionPercent = checklistStats.total > 0 
    ? Math.round((checklistStats.completed / checklistStats.total) * 100) 
    : 0;

  // Get time of day greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Get environmental context from weather
  const getEnvironmentalContext = () => {
    if (!weather || weather.error) return null;
    
    const contexts = [];
    if (weather.humidity < 30) contexts.push('Dry conditions');
    if (weather.humidity > 80) contexts.push('High humidity');
    if (weather.temp > 90) contexts.push('Heat advisory');
    if (weather.temp < 32) contexts.push('Freeze risk');
    if (weather.wind_speed > 20) contexts.push('High winds');
    
    return contexts.length > 0 ? contexts.join(' • ') : 'Conditions normal';
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in paper-texture min-h-screen" data-testid="daily-advisor-loading">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in paper-texture min-h-screen pb-6" data-testid="daily-advisor">
      {/* Offline/Cached indicator */}
      {isFromCache && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50/80 border-l-3 border-amber-400 text-amber-800 text-sm">
          <WifiOff className="w-4 h-4" />
          <span>Showing cached data</span>
          {isOnline && (
            <button 
              onClick={() => fetchAdvice(true)} 
              className="ml-auto text-amber-600 hover:text-amber-800 font-medium text-xs"
            >
              Refresh
            </button>
          )}
        </div>
      )}
      
      {/* ========================================
          SECTION 1: TODAY OVERVIEW (Primary Panel)
          Combined: Farm name, Weather, Environmental context, Priority work, Chore progress
          ======================================== */}
      <section className="space-y-4" data-testid="today-overview-section">
        {/* Farm Header with Weather */}
        <div className="pt-2">
          <div className="flex items-start justify-between">
            <div>
              <p className="section-header text-xs mb-1">TODAY AT</p>
              <h1 className="font-heading text-xl font-bold text-foreground leading-tight">
                {user?.farm_name || 'Your Farm'}
              </h1>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => { fetchAdvice(true); fetchChecklist(); fetchWeather(); fetchWeatherSuggestions(); fetchFarmReminders(); }}
              disabled={refreshing}
              className="text-muted-foreground hover:text-primary -mt-1"
              data-testid="refresh-advice-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          
          {/* Weather Summary - Inline */}
          {!weatherLoading && weather && !weather.error && (
            <div className="mt-3 flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Thermometer className="w-4 h-4 text-primary" />
                <span className="font-semibold text-lg">{Math.round(weather.temp)}°F</span>
                <span className="text-muted-foreground capitalize">{weather.description}</span>
              </div>
              <div className="flex items-center gap-3 text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Droplets className="w-3.5 h-3.5" />
                  {weather.humidity}%
                </span>
                <span className="flex items-center gap-1">
                  <Wind className="w-3.5 h-3.5" />
                  {Math.round(weather.wind_speed)} mph
                </span>
              </div>
            </div>
          )}
          
          {/* Environmental Context Line */}
          {getEnvironmentalContext() && (
            <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5">
              <MapPin className="w-3 h-3" />
              {getEnvironmentalContext()}
              {weather?.location && <span className="ml-2">• {weather.location}</span>}
            </p>
          )}
        </div>

        {/* Priority Work Today - Top 3 Actions */}
        {actions.length > 0 && (
          <div className="field-panel" data-testid="priority-work-panel">
            <h3 className="section-header flex items-center gap-2 mb-3">
              <Sun className="w-4 h-4 text-primary" />
              Priority Work Today
            </h3>
            <div className="space-y-2">
              {actions.map((action, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 py-2 journal-entry"
                >
                  <span className="w-5 h-5 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">
                    {index + 1}
                  </span>
                  <p className="text-sm text-foreground leading-relaxed">{action}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Daily Chores Progress - Compact */}
        {checklist.length > 0 && (
          <div className="field-panel" data-testid="daily-chores-progress">
            <div className="flex items-center justify-between mb-2">
              <h3 className="section-header flex items-center gap-2 mb-0">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                Daily Chores
              </h3>
              <div className="flex items-center gap-3">
                {checklistStats.streak > 0 && (
                  <div className="flex items-center gap-1 text-amber-600" title="Day streak">
                    <Flame className="w-3.5 h-3.5" />
                    <span className="text-xs font-bold">{checklistStats.streak}</span>
                  </div>
                )}
                <span className="text-sm font-medium text-muted-foreground">
                  {checklistStats.completed}/{checklistStats.total}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCalendar(true)}
                  className="text-muted-foreground hover:text-primary h-7 w-7 p-0"
                  title="View history"
                  data-testid="view-calendar-btn"
                >
                  <CalendarDays className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Progress bar */}
            <div className="h-2 bg-muted/50 rounded-full overflow-hidden mb-3">
              <div 
                className="h-full bg-primary transition-all duration-500 rounded-full"
                style={{ width: `${completionPercent}%` }}
              />
            </div>
            
            {/* Chore list */}
            {checklistLoading ? (
              <div className="flex items-center justify-center py-3">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-1 max-h-[200px] overflow-y-auto" data-testid="checklist-scroll-container">
                {checklist.map((chore) => (
                  <label
                    key={chore.id}
                    className={`flex items-center gap-3 py-2 px-2 -mx-2 rounded cursor-pointer transition-colors ${
                      chore.completed 
                        ? 'text-muted-foreground' 
                        : 'hover:bg-muted/30'
                    }`}
                    data-testid={`chore-${chore.id}`}
                  >
                    <Checkbox
                      checked={chore.completed}
                      onCheckedChange={() => toggleChore(chore.id, chore.completed)}
                      className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                    />
                    <span className={`text-sm flex-1 ${chore.completed ? 'line-through' : ''}`}>
                      {chore.name}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* ========================================
          SECTION 2: DAILY CHORES (Setup prompt if empty)
          ======================================== */}
      {!checklistLoading && checklist.length === 0 && (
        <section className="field-panel border-dashed" data-testid="setup-checklist-prompt">
          <div className="text-center py-4">
            <CheckCircle2 className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <h3 className="font-medium text-foreground mb-1 text-sm">Set Up Daily Chores</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Track your daily farm tasks and build consistency.
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.location.href = '/app/settings'}
              data-testid="setup-checklist-btn"
            >
              Go to Settings
            </Button>
          </div>
        </section>
      )}

      {/* ========================================
          SECTION 3: FOUNDATION ANIMALS
          Renamed from "Star Animals" - breeding/production animals
          ======================================== */}
      <section data-testid="foundation-animals-section">
        <StarAnimalsGallery 
          getAuthHeader={getAuthHeader}
          onAnimalClick={(animal) => {
            toast.info(`Go to Livestock or Poultry to see ${animal.name}'s full profile`);
          }}
          sectionTitle="Foundation Animals"
        />
      </section>

      {/* ========================================
          SECTION 4: FIELD CONDITIONS
          Renamed from "Weather-Based Tasks"
          ======================================== */}
      {activeSuggestions.length > 0 && (
        <section className="space-y-3" data-testid="field-conditions-section">
          <h2 className="section-header flex items-center gap-2">
            <Leaf className="w-4 h-4 text-primary" />
            Field Conditions
          </h2>
          
          {suggestionsLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-2">
              {activeSuggestions.map((suggestion) => {
                const IconComponent = suggestionIcons[suggestion.icon] || AlertTriangle;
                
                return (
                  <div 
                    key={suggestion.id}
                    className={`flex items-start gap-3 py-3 px-3 rounded-md journal-entry border-0 ${
                      suggestion.priority === 'high' ? 'field-badge-warning bg-[hsl(15,42%,52%)]/5' :
                      suggestion.priority === 'medium' ? 'bg-amber-50/50' :
                      'bg-muted/20'
                    }`}
                    data-testid={`field-condition-${suggestion.id}`}
                  >
                    <IconComponent className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      suggestion.priority === 'high' ? 'text-[hsl(15,42%,48%)]' :
                      suggestion.priority === 'medium' ? 'text-amber-600' :
                      'text-primary'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-foreground">{suggestion.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{suggestion.reason}</p>
                    </div>
                    <button
                      onClick={() => dismissSuggestion(suggestion.id)}
                      className="text-muted-foreground/50 hover:text-muted-foreground transition-colors p-1 -m-1"
                      title="Dismiss"
                      data-testid={`dismiss-condition-${suggestion.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* ========================================
          SECTION 5: THINGS TO WATCH THIS WEEK
          Renamed from "This Week's Watchouts"
          ======================================== */}
      {watchouts.length > 0 && (
        <section className="space-y-3" data-testid="watch-this-week-section">
          <h2 className="section-header flex items-center gap-2">
            <Eye className="w-4 h-4 text-[hsl(15,42%,48%)]" />
            Things To Watch This Week
          </h2>
          
          <div className="space-y-2">
            {watchouts.map((watchout, index) => (
              <div 
                key={index}
                className="flex items-start gap-3 py-2 journal-entry"
              >
                <AlertTriangle className="w-4 h-4 text-[hsl(15,42%,52%)] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-foreground">{watchout}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ========================================
          UPCOMING EVENTS (Secondary)
          Farm reminders for breeding, birthdays, etc.
          ======================================== */}
      {farmReminders.length > 0 && (
        <section className="space-y-3" data-testid="upcoming-events-section">
          <h2 className="section-header flex items-center gap-2">
            <Bell className="w-4 h-4 text-primary" />
            Upcoming Events
            <span className="ml-auto text-xs font-normal">Next 2 weeks</span>
          </h2>
          
          {remindersLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-2 max-h-[180px] overflow-y-auto">
              {farmReminders.slice(0, 6).map((reminder, index) => {
                const isToday = reminder.date === todayLocal();
                const isTomorrow = reminder.date === daysAgoLocal(-1);
                const dateLabel = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : new Date(reminder.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
                
                const categoryIcons = {
                  breeding: Heart,
                  birthdays: Baby,
                  health: CheckCircle2,
                };
                const Icon = categoryIcons[reminder.category] || Calendar;
                
                return (
                  <div 
                    key={reminder.id || `reminder-${index}`}
                    className={`flex items-start gap-3 py-2 journal-entry ${isToday ? 'bg-primary/5 -mx-2 px-2 rounded' : ''}`}
                    data-testid={`reminder-${index}`}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isToday ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${isToday ? 'font-medium text-primary' : 'text-foreground'}`}>
                        {reminder.title}
                      </p>
                      {reminder.description && (
                        <p className="text-xs text-muted-foreground truncate">{reminder.description}</p>
                      )}
                    </div>
                    <span className={`text-xs flex-shrink-0 ${isToday ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                      {dateLabel}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          
          {farmReminders.length > 6 && (
            <p className="text-xs text-muted-foreground text-center pt-1">
              +{farmReminders.length - 6} more events
            </p>
          )}
        </section>
      )}

      {advice?.fallback && (
        <p className="text-xs text-muted-foreground text-center pt-2">
          Using cached data. Connect to refresh.
        </p>
      )}

      {/* Chore History Calendar Dialog */}
      <Dialog open={showCalendar} onOpenChange={setShowCalendar}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <CalendarDays className="w-5 h-5 text-primary" />
              Chore History
            </DialogTitle>
          </DialogHeader>
          <ChoreCalendar onClose={() => setShowCalendar(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DailyAdvisor;
